<script lang="ts" setup>
import { Product } from '@/services/models/implementations/Product.ts'
import EnlargeBox from '@/components/EnlargeBox.vue'
import { ProductResponse } from '@/services/models/ProductResponse.ts'
import { copyToClipboard, writeJsonToClipboard } from '@/helper/tools.ts'

defineProps<{
  product: Product | ProductResponse
  showDetails: boolean
}>()
</script>

<template>
  <div
    class="fTable__item"
    @click.exact.alt.prevent="copyToClipboard(product.id)"
    @click.exact.alt.ctrl.shift.prevent="writeJsonToClipboard(product)">
    <div class="fTable__tr">
      <slot name="input" />

      <div class="fTable__td column is--product">
        <div v-if="product.pictureUrl !== 'none'" class="product__img fallback-bg">
          <img v-if="product.pictureUrl" :alt="product.name" :src="product.pictureUrl" loading="lazy">
        </div>

        <div class="fTable__product-infos">
          <h3 class="product__title">
            {{ product.name }}
          </h3>
        </div>
      </div>

      <div class="fTable__td column is--details hide-mobile">
        <slot name="details-btn" />
      </div>

      <slot name="quantity-input" />
    </div>

    <EnlargeBox
      v-if="product.productAdvantages?.length > 0"
      :key="`enlarge-box-${product.id}`"
      :is-open="showDetails"
      class="fTable__tr fTable__details details"
    >
      <template #content>
        <div class="column">
          <ul v-if="product.productAdvantages" class="check-list product__features">
            <li v-for="index in Math.min(3, product.productAdvantages.length)" :key="index">
              {{ product.productAdvantages[index - 1] }}
            </li>
          </ul>
        </div>
      </template>
    </EnlargeBox>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/style/helpers/include-media';

.details {
  .column {
    margin-top: 0;
  }

  .product__features {
    margin: 0;
  }
}

.fTable.is--products-table .fTable__td.is--product {
  padding-top: 1rem;
  padding-bottom: 1rem;
  padding-right: 2rem;
  min-width: auto;
  max-width: none;
  flex: 1;
  align-items: center;
  overflow: hidden;

  .product__title {
    font-size: 1.25rem;
    margin-bottom: 0;

    @include media('<tablet') {
      word-wrap: break-word;
    }
  }

  .product__img {
    min-width: 6rem;
    max-width: 6rem;
    height: 6rem;
    overflow: hidden;
    margin-right: 2rem;

    @include themify($themes) {
      border-radius: rem-calc(themed('cardBorderRadius'));
    }

    @include media('<tablet') {
      display: none;
    }

    &.fallback-bg {
      background: radial-gradient($imageGradient);
    }

    img {
      object-fit: cover;
      object-position: center;
      width: 100%;
      height: 6rem;
      transform: scale3d(1.15, 1.15, 1);
    }
  }
}
</style>
